import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  Download,
  ExternalLink,
  File,
  FileText,
  FolderGit2,
  Image as ImageIcon,
  Link as LinkIcon,
  Presentation,
  Video,
} from 'lucide-react';
import { formatDate, formatFileSize } from '../../../utils/format';

const TYPES = new Map([
  ['pdf', { label: 'PDF', icon: FileText }],
  ['doc', { label: 'Document', icon: FileText }],
  ['docx', { label: 'Document', icon: FileText }],
  ['pptx', { label: 'Slides', icon: Presentation }],
  ['ppt', { label: 'Slides', icon: Presentation }],
  ['repository', { label: 'GitHub repository', icon: FolderGit2 }],
  ['video', { label: 'Video', icon: Video }],
  ['image', { label: 'Image', icon: ImageIcon }],
  ['link', { label: 'Link', icon: LinkIcon }],
]);

/**
 * One deliverable item. Order data carries no links: `onOpen` asks the server, which checks
 * ownership and access expiry and returns the link. `expiresAt` is the order's access expiry,
 * which is what the server enforces.
 */
const DeliverableCard = ({ item, onOpen, expiresAt }) => {
  const descriptionRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const [opening, setOpening] = useState(false);

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (!el || expanded) return;
    setClamped(el.scrollHeight > el.clientHeight + 1);
  }, [item.description, expanded]);

  const fileType = String(item.fileType || '').toLowerCase();
  const type = TYPES.get(fileType) || { label: fileType ? fileType.toUpperCase() : 'File', icon: File };
  const Icon = type.icon;
  const isRepo = fileType === 'repository';
  const opensLink = isRepo || fileType === 'link' || fileType === 'video';
  const size = item.fileSizeInBytes > 0 ? formatFileSize(item.fileSizeInBytes / (1024 * 1024)) : null;
  const expired = expiresAt ? new Date(expiresAt) < new Date() : false;

  const handleOpen = async () => {
    if (opening) return;
    setOpening(true);
    try {
      await onOpen?.();
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="pk-deliverable">
      <span className="pk-deliverable__icon" aria-hidden="true">
        <Icon />
      </span>
      <div className="pk-deliverable__body">
        <span className="pk-deliverable__title">{item.title || type.label}</span>
        <span className="pk-deliverable__meta">
          {type.label}
          {size && ` · ${size}`}
        </span>
        {item.description && (
          <>
            <p ref={descriptionRef} className={`pk-deliverable__desc${expanded ? '' : ' is-clamped'}`}>
              {item.description}
            </p>
            {(clamped || expanded) && (
              <button
                type="button"
                className="pk-deliverable__more"
                aria-expanded={expanded}
                onClick={() => setExpanded(value => !value)}
              >
                {expanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </>
        )}
      </div>
      <div className="pk-deliverable__action">
        {expired ? (
          <span className="pk-deliverable__expired">Access ended {formatDate(expiresAt)}</span>
        ) : (
          <button
            type="button"
            className="pk-btn pk-btn--secondary pk-btn--sm"
            onClick={handleOpen}
            aria-busy={opening || undefined}
            aria-label={`${opensLink ? 'Open' : 'Download'} ${item.title || type.label}`}
          >
            {opensLink ? <ExternalLink aria-hidden="true" /> : <Download aria-hidden="true" />}
            {opensLink ? 'Open' : 'Download'}
          </button>
        )}
      </div>
    </div>
  );
};

export default React.memo(DeliverableCard);
